/**
 * Finance › Ledger rules — the rules that code each purchase invoice line when its purchase invoice is
 * captured (Ben, 3 Oct; spec 05 §9), and each CIS sales line when its invoice
 * is raised (sales ledger SL2, doc 50 §4.9). Last in the Finance menu, and
 * where the month end's "fix at source" sends a goods-received line or a purchase invoice
 * line.
 *
 * This page owns the shell, the routes and the Spread over tab; the Coding tab is
 * the purchase ledger's `CodingRulesTab` and Sales coding the sales ledger's
 * `SalesCodingRulesTab`, both mounted here.
 */

import { Link, useParams } from "react-router";
import {
  LEDGER_RULES_TABS,
  paths,
  type LedgerRulesTab,
} from "@shared-core/routing/routes";
import { SPREAD_OVER_WORDS } from "@shared-core/domain/spread-over";
import { SpreadOverRulesTab } from "../app/components/domain/accruals/SpreadOverRulesTab";
import { SalesCodingRulesTab } from "../app/components/domain/sales-ledger/SalesCodingRulesTab";
import { CodingRulesTab } from "../app/components/domain/purchasing/CodingRulesTab";
import { cn } from "../app/components/primitives/utils";

const TAB_LABELS: Record<LedgerRulesTab, string> = {
  coding: "Coding",
  "spread-over": SPREAD_OVER_WORDS.name,
  "sales-coding": "Sales coding",
};

export function LedgerRulesPage() {
  const { tab: rawTab } = useParams();
  const tab: LedgerRulesTab | null = !rawTab
    ? "coding"
    : (LEDGER_RULES_TABS as readonly string[]).includes(rawTab)
      ? (rawTab as LedgerRulesTab)
      : null;

  return (
    <div className="space-y-6">
      <header>
        <h1 className="text-foreground text-2xl">Ledger rules</h1>
        <p className="text-muted-foreground mt-1 max-w-2xl text-sm">
          The rules that code each purchase invoice line when its purchase
          invoice is captured (which Xero account it goes to, and whether it is
          spread over a period), and each sales line when its invoice is raised.
        </p>
      </header>

      <nav aria-label="Ledger rules" className="flex flex-wrap gap-1 border-b">
        {LEDGER_RULES_TABS.map((t) => (
          <Link
            key={t}
            to={paths.financeLedgerRules({ tab: t })}
            aria-current={t === tab ? "page" : undefined}
            className={cn(
              "-mb-px border-b-2 px-3 py-2 text-sm transition-colors",
              t === tab
                ? "border-primary text-foreground font-medium"
                : "text-muted-foreground hover:text-foreground border-transparent",
            )}
          >
            {TAB_LABELS[t]}
          </Link>
        ))}
      </nav>

      {tab === "coding" && <CodingRulesTab />}
      {tab === "spread-over" && <SpreadOverRulesTab />}
      {tab === "sales-coding" && <SalesCodingRulesTab />}
      {tab === null && (
        <p className="text-muted-foreground text-sm">
          The Ledger rules page has no “{rawTab}” tab.
        </p>
      )}
    </div>
  );
}
